import React from 'react';
import { ArrowLeft, Phone, Calendar, Clock } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import Footer from './home/Footer';
import { ARTICLES, getArticle } from '../data/articles';
import { useSEO } from '../hooks/useSEO';

/** Mise en page commune des articles de blog (nav, en-tête, SEO Article, CTA, autres articles, footer). */
const BlogLayout = ({ id, description, imageAlt, children }) => {
  const navigate = useNavigate();
  const article = getArticle(id);
  const autres = ARTICLES.filter((a) => a.id !== id).slice(0, 3);

  useSEO({
    title: `${article.title} | Blog Suro Phone`,
    description,
    path: `/blog/${id}`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description,
      image: article.image,
      datePublished: article.dateIso,
      dateModified: article.dateIso,
      inLanguage: 'fr-FR',
      author: { '@type': 'Organization', name: 'Suro Phone', url: 'https://surophone.fr' },
      publisher: { '@type': 'Organization', name: 'Suro Phone', url: 'https://surophone.fr' },
      mainEntityOfPage: `https://surophone.fr/blog/${id}`,
    },
  });

  return (
    <div className="min-h-screen bg-white" data-testid={`blog-article-${id}`}>
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/blog')} data-testid="blog-back-btn" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Retour au blog</span>
          </button>
          <a href="tel:0773601521" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors">
            <Phone className="w-4 h-4" />
            <span className="font-medium">07.73.60.15.21</span>
          </a>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
          <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{article.date}</span>
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{article.readTime} de lecture</span>
        </div>

        <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-testid="blog-article-h1">{article.title}</h1>

        <img src={article.image.replace('w=800', 'w=1200')} alt={imageAlt} className="w-full h-56 md:h-96 object-cover rounded-lg mb-8" loading="lazy" />

        <div className="prose prose-lg max-w-none">{children}</div>

        <div className="mt-12 bg-blue-600 text-white rounded-2xl p-8 text-center" data-testid="blog-cta">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Besoin d'un réparateur à Saint-Chamond ou Saint-Étienne ?</h2>
          <p className="text-blue-100 mb-6">Devis gratuit et immédiat • Réparé le jour même • Garantie 3 mois • À domicile dans toute la Loire (42)</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:0773601521" className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg">
              <Phone className="w-5 h-5" /> 07.73.60.15.21
            </a>
            <Link to="/rendez-vous" className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg">
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </article>

      <section className="bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">À lire aussi</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {autres.map((a) => (
              <Link key={a.id} to={`/blog/${a.id}`} data-testid={`blog-related-${a.id}`} className="block bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
                <img src={a.image} alt={a.title} className="w-full h-32 object-cover" loading="lazy" />
                <div className="p-4">
                  <p className="font-semibold text-gray-900 text-sm leading-snug">{a.title}</p>
                  <p className="text-xs text-gray-500 mt-2">{a.readTime} de lecture</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/blog"><Button variant="outline" className="px-8 py-4 rounded-xl font-bold">Tous les articles</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogLayout;
